"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseStringValueToSqlPrimitive = exports.convertOptionalString = exports.convertStrictNonEmptyString = exports.convertOptionalDateTimeStamp = exports.convertStrictDateTimeStamp = exports.convertOptionalDateFactory = exports.convertStrictDateFactory = exports.convertOptionalPercentageToFraction = exports.convertStrictPercentageToFraction = exports.convertOptionalCurrencyFactory = exports.nullOrCurrencyStrToNumber = exports.convertStrictCurrencyFactory = exports.convertStringBoolean = exports.convertOptionalInteger = exports.convertStrictInteger = exports.convertOptionalNumber = exports.convertStrictNumber = exports.nullOrMappedValue = exports.currencyStrToNumber = exports.isEmptyString = void 0;
const accounting_1 = __importDefault(require("accounting"));
const lodash_1 = __importDefault(require("lodash"));
const finley_types_1 = require("@finley-technologies/finley-types");
const date_1 = __importDefault(require("../date"));
const error_1 = require("../error");
const typeguards_1 = require("../typeguards");
const isEmptyString = (val) => !/\S/.test(val);
exports.isEmptyString = isEmptyString;
// Using new accounting lib, since currency-formatter is limited and in maintenance mode
const currencyStrToNumber = (val, code) => {
    const currencyCodeRegExpMap = {
        [finley_types_1.Bank.Currency
            .USD]: /^\s*-? *\$?\s*(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,})?\s*$/,
        [finley_types_1.Bank.Currency
            .MXN]: /^\s*-? *(?:MX)?\s*\$?\s*(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,})?\s*$/,
        [finley_types_1.Bank.Currency
            .EUR]: /^\s*-? *(?:EUR|€)?\s*(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,})?\s*(?:EUR)?\s*$/,
        [finley_types_1.Bank.Currency
            .GBP]: /^\s*-? *(?:£)?\s*(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,})?\s*$/,
    };
    const currencyCodeRegexp = currencyCodeRegExpMap[code];
    if (!currencyCodeRegexp) {
        return null;
    }
    // if a value has matching parens, replace with a negative value
    const parenMatch = String(val).match(/^\s*\$?\s*\((.*)\)\s*$/);
    if (parenMatch) {
        // eslint-disable-next-line no-param-reassign
        val = `-${parenMatch[1]}`;
    }
    // '$ -' is a format that we can get, which means 0 in some financial formats
    const zeroValueMatch = String(val).match(/(^[^0-9]*)-\s*$/);
    if (zeroValueMatch) {
        // eslint-disable-next-line no-param-reassign
        val = `${zeroValueMatch[1]}0`;
    }
    if (!currencyCodeRegexp.exec(val)) {
        return null;
    }
    return accounting_1.default.unformat(val);
};
exports.currencyStrToNumber = currencyStrToNumber;
// if null, return null. otherwise return the `mappedValue
const nullOrMappedValue = (val, mappedvalue) => {
    return lodash_1.default.isNil(val) ? null : mappedvalue;
};
exports.nullOrMappedValue = nullOrMappedValue;
// acceptable non-numeric values to remove
const removeableNumberChars = /\s|%|,/g;
const cleanInitialValue = (val) => {
    return String(val).replace(removeableNumberChars, '');
};
const isNilOrEmpty = (val) => {
    return lodash_1.default.isNil(val) || val === '';
};
const convertStrictNumber = (val, key) => {
    const number = Number(cleanInitialValue(val));
    if (lodash_1.default.isNaN(number)) {
        let errMsg = `Expected a number in converting \`${val}\` in convertStrictNumber`;
        if (key) {
            errMsg = `${errMsg} for key \`${key}\``;
        }
        throw Error(errMsg);
    }
    return number;
};
exports.convertStrictNumber = convertStrictNumber;
const convertOptionalNumber = (val, key) => {
    if (isNilOrEmpty(val)) {
        return null;
    }
    const number = (0, exports.convertStrictNumber)(val, key);
    return number;
};
exports.convertOptionalNumber = convertOptionalNumber;
const convertStrictInteger = (val, key) => {
    const integer = Number(cleanInitialValue(val));
    // isInteger does a null check
    if (!Number.isInteger(integer)) {
        let errMsg = `Expected a integer in converting \`${val}\` in convertStrictInteger`;
        if (key) {
            errMsg = `${errMsg} for key \`${key}\``;
        }
        throw Error(errMsg);
    }
    return integer;
};
exports.convertStrictInteger = convertStrictInteger;
const convertOptionalInteger = (val, key) => {
    if (isNilOrEmpty(val)) {
        return null;
    }
    const integer = (0, exports.convertStrictInteger)(val, key);
    return integer;
};
exports.convertOptionalInteger = convertOptionalInteger;
/**
 * Convert e.g. "TRUE" to a boolean `true`
 * @param booleanString "TRUE"
 * @returns boolean, e.g. `true`
 */
const convertStringBoolean = (value, trueString) => {
    const caseInsensitiveTrueString = trueString && lodash_1.default.toUpper(trueString);
    return lodash_1.default.toUpper(value || '') === (caseInsensitiveTrueString || 'TRUE');
};
exports.convertStringBoolean = convertStringBoolean;
const convertStrictCurrencyFactory = (currencyCode) => (val) => {
    const currencyVal = (0, exports.currencyStrToNumber)(val, currencyCode);
    (0, error_1.finleyAssert)(lodash_1.default.isFinite(currencyVal), new error_1.FinleyError(`Expected convertStrictCurrencyFactory to convert ${val}`));
    return currencyVal;
};
exports.convertStrictCurrencyFactory = convertStrictCurrencyFactory;
const nullOrCurrencyStrToNumber = (val, code) => {
    if (isNilOrEmpty(val)) {
        return null;
    }
    return (0, exports.currencyStrToNumber)(val, code);
};
exports.nullOrCurrencyStrToNumber = nullOrCurrencyStrToNumber;
const convertOptionalCurrencyFactory = (currencyCode) => (val) => (0, exports.nullOrCurrencyStrToNumber)(val, currencyCode);
exports.convertOptionalCurrencyFactory = convertOptionalCurrencyFactory;
/**
 * Convert percent to float by dividing by 100
 * use convertStrictNumber is you do not want to divide val by 100
 * @returns float e.g. val / 100
 */
const convertStrictPercentageToFraction = (val) => {
    const percentage = Number(cleanInitialValue(val));
    (0, error_1.finleyAssert)(!lodash_1.default.isNaN(percentage), new error_1.FinleyError(`Expected a percentage in converting \`${val}\` in convertStrictPercentageToFraction`));
    return percentage / 100.0;
};
exports.convertStrictPercentageToFraction = convertStrictPercentageToFraction;
/**
 * Convert percent to float by dividing by 100
 * use convertOptionalNumber is you do not want to divide val by 100
 * @returns float e.g. val / 100
 */
const convertOptionalPercentageToFraction = (val) => {
    if (isNilOrEmpty(val)) {
        return null;
    }
    return (0, exports.convertStrictPercentageToFraction)(val);
};
exports.convertOptionalPercentageToFraction = convertOptionalPercentageToFraction;
const convertStrictDateFactory = (dateFormat) => (val, key) => {
    try {
        return date_1.default.strToFullDateStr(val, dateFormat);
    }
    catch (e) {
        let errMsg = `Expected a valid date string converting \`${val}\` in convertStrictDateFactory to format ${dateFormat}`;
        if (key) {
            errMsg = `${errMsg} for key \`${key}\``;
        }
        throw Error(errMsg);
    }
};
exports.convertStrictDateFactory = convertStrictDateFactory;
const convertOptionalDateFactory = (dateFormat) => (val, key) => {
    try {
        return date_1.default.strToFullDateStrOptional(val, dateFormat);
    }
    catch (e) {
        let errMsg = `Expected a valid date string converting \`${val}\` in convertOptionalDateFactory to format ${dateFormat}`;
        if (key) {
            errMsg = `${errMsg} for key \`${key}\``;
        }
        throw Error(errMsg);
    }
};
exports.convertOptionalDateFactory = convertOptionalDateFactory;
const convertStrictDateTimeStamp = (dateTimeStamp) => {
    // this confirms that the timestamp has at least HH:MM:SS
    const timeStampRegex = /\d*:\d*:\d*/;
    (0, error_1.finleyAssert)(timeStampRegex.exec(dateTimeStamp), new error_1.FinleyError('Expected this value to be a timestamp'));
    return new Date(dateTimeStamp);
};
exports.convertStrictDateTimeStamp = convertStrictDateTimeStamp;
const convertOptionalDateTimeStamp = (dateTimeStamp) => {
    if (!dateTimeStamp) {
        return null;
    }
    return (0, exports.convertStrictDateTimeStamp)(dateTimeStamp);
};
exports.convertOptionalDateTimeStamp = convertOptionalDateTimeStamp;
const convertStrictNonEmptyString = (val, key) => {
    if (!lodash_1.default.isString(val) || val.length === 0) {
        let errMsg = `Expected a string in converting \`${val}\` in convertStrictNonEmptyString`;
        if (key) {
            errMsg = `${errMsg} for key \`${key}\``;
        }
        throw Error(errMsg);
    }
    return val;
};
exports.convertStrictNonEmptyString = convertStrictNonEmptyString;
/**
 * converts null-ish, empty string, or 'NulL' to null type, or returns string
 */
const convertOptionalString = (val) => {
    if (!val)
        return null;
    if (val.length === 0)
        return null;
    if (lodash_1.default.toUpper(lodash_1.default.trim(val)) === 'NULL')
        return null;
    return val;
};
exports.convertOptionalString = convertOptionalString;
const parseStringValueToSqlPrimitive = (params) => {
    const { value, parseString } = params;
    // If don't need to parse or already non-string, just return value
    if (!parseString || typeof value !== 'string') {
        return value;
    }
    if ((0, typeguards_1.isFullDateStr)(value)) {
        return value;
    }
    if (value.match(/^[-+]?[0-9]*\.[0-9]*$/) && lodash_1.default.isFinite(parseFloat(value))) {
        return parseFloat(value);
    }
    if (value.match(/^[-+]?[0-9]+$/) && lodash_1.default.isFinite(parseInt(value, 10))) {
        return parseInt(value, 10);
    }
    if (value.toUpperCase() === 'TRUE') {
        return true;
    }
    if (value.toUpperCase() === 'FALSE') {
        return false;
    }
    return value;
};
exports.parseStringValueToSqlPrimitive = parseStringValueToSqlPrimitive;
//# sourceMappingURL=index.js.map